

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useParams } from 'react-router-dom';
 
const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const decodeRole = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.role || null;
  } catch {
    return null;
  }
};
 
// ─── Polling config ────────────────────────────────────────────────────────────
const POLL_INITIAL_DELAY_MS  = 500;   // give the device time to register the command
const POLL_MAX_INTERVAL_MS   = 1500;  // poll at most every 1.5 s once in steady state
const POLL_BACKOFF_FACTOR    = 1.4;   // gentler growth so polls stay frequent longer
const POLL_TIMEOUT_MS        = 60000; // 60 s — wait up to 1 minute for hardware confirmation
const POLL_MAX_REVERT_GRACE  = 2;     // allow this many old-state readings before declaring revert
// ───────────────────────────────────────────────────────────────────────────────
 
export const AuthProvider = ({ children }) => {
  const initialAccessToken =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token") ||
    "";
 
  const [isAuthenticated, setIsAuthenticated] = useState(!!initialAccessToken);
  const [userRole, setUserRole]               = useState(() => initialAccessToken ? decodeRole(initialAccessToken) : null);
  const [accessToken, setAccessToken]         = useState(initialAccessToken);
  const [refreshToken, setRefreshToken]       = useState(
    localStorage.getItem("refresh_token") ||
    sessionStorage.getItem("refresh_token") ||
    ""
  );
  const [loading, setLoading]                       = useState(true);
  const [pumpStatus, setPumpStatus]                 = useState(false);
  const [data, setData]                             = useState([]);
  const [value, setValue]                           = useState("");
  const [isOnline, setIsOnline]                     = useState(false);
  const [duration, setDuration]                     = useState("7");
  const [dashboardData, setDashboardData]           = useState("");
  const [pumps, setPumps]                           = useState([]);
  const [consumers, setConsumers]                   = useState([]);
  const [complaints, setComplaints]                 = useState([]);
  const [pumpRunningStatus, setPumpRunningStatus]   = useState("");
  const [autoRefresh, setAutoRefresh]               = useState(true);
  const [pumpStats, setPumpStats]                   = useState("");
  const [pumpSessions, setPumpSessions]             = useState([]);
  const [pumpsWithImei, setPumpsWithImei]           = useState([]);
  const [pumpToUpdate, setPumpToUpdate]             = useState([]);
  const [pumpLocations, setPumpLocations]           = useState([]);
  const [error, setError]                           = useState(null);
 
  // ── Refs used for non-reactive bookkeeping ─────────────────────────────────
  // Keeps the latest accessToken/refreshToken available inside async callbacks
  // without stale-closure problems.
  const accessTokenRef   = useRef(accessToken);
  const refreshTokenRef  = useRef(refreshToken);
 
  // Cancels any in-flight changePumpRunningStatus operation when a new one
  // starts or the component unmounts.
  const statusChangeAbortRef = useRef(null);
 
  // Tracks whether a status-change operation is already running so rapid
  // double-clicks are safely ignored.
  const statusChangeInFlightRef = useRef(false);
 
  // Refs are kept in sync via state effects as a backstop, but setTokens
  // also writes them synchronously so any effect that fires in the same
  // commit cycle already sees the fresh token.
  useEffect(() => { accessTokenRef.current  = accessToken;  }, [accessToken]);
  useEffect(() => { refreshTokenRef.current = refreshToken; }, [refreshToken]);
  // ───────────────────────────────────────────────────────────────────────────

  // ── Token helpers ──────────────────────────────────────────────────────────
  const setTokens = (newAccessToken, newRefreshToken, remember) => {
    // Update refs immediately so authorizedFetch called in the same render
    // cycle (e.g. from a child component's useEffect) already has the token.
    accessTokenRef.current  = newAccessToken;
    refreshTokenRef.current = newRefreshToken;
    setAccessToken(newAccessToken);
    setRefreshToken(newRefreshToken);
    setUserRole(decodeRole(newAccessToken));
    if (remember) {
      localStorage.setItem("access_token", newAccessToken);
      localStorage.setItem("refresh_token", newRefreshToken);
    } else {
      sessionStorage.setItem("access_token", newAccessToken);
      sessionStorage.setItem("refresh_token", newRefreshToken);
    }
    setIsAuthenticated(true);
  };
 
  const clearTokens = () => {
    accessTokenRef.current  = "";
    refreshTokenRef.current = "";
    setAccessToken("");
    setRefreshToken("");
    setUserRole(null);
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("refresh_token");
    setIsAuthenticated(false);
  };
 
  const refreshAccessToken = useCallback(async () => {
    try {
      const response = await fetch("https://api.servotechsolar.in/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshTokenRef.current }),
      });
      if (response.ok) {
        const data = await response.json();
        setTokens(
          data.access_token,
          refreshTokenRef.current,
          localStorage.getItem("access_token") !== null
        );
        return data.access_token;
      }
      clearTokens();
      return null;
    } catch {
      clearTokens();
      return null;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
 
  // ── Authorized fetch (uses refs so it is always fresh) ────────────────────
  const authorizedFetch = useCallback(async (url, options = {}, signal) => {
    let token = accessTokenRef.current;
 
    if (!token) {
      token = await refreshAccessToken();
      if (!token) throw new Error("Authentication failed");
    }
 
    const buildHeaders = (t) => {
      const headers = { ...options.headers, Authorization: `Bearer ${t}` };
      // Only set Content-Type when there is an actual body.
      // – FormData: browser sets it automatically (with multipart boundary).
      // – No body (GET/DELETE/HEAD): sending Content-Type confuses some
      //   backends (e.g. Flask calls request.get_json(), finds an empty body,
      //   and throws a 500).
      if (options.body && !(options.body instanceof FormData)) {
        headers["Content-Type"] = "application/json";
      }
      return headers;
    };
 
    const doFetch = (t) =>
      fetch(`https://api.servotechsolar.in${url}`, {
        ...options,
        headers: buildHeaders(t),
        signal, // honours AbortController
      });
 
    try {
      const response = await doFetch(token);
 
      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        if (newToken) return doFetch(newToken);
        throw new Error("Authentication failed");
      }
 
      return response;
    } catch (err) {
      // Don't log abort errors — they are intentional.
      if (err.name !== "AbortError") console.error("Fetch error:", err);
      throw err;
    }
  }, [refreshAccessToken]);
 
  // ── Token verification on mount ───────────────────────────────────────────
  useEffect(() => {
    (async () => {
      if (accessTokenRef.current) {
        setIsAuthenticated(true);
        // Only fetch locations when we know we have a valid token
        try {
          const res = await authorizedFetch(`/pump/location`);
          setPumpLocations(await res.json());
        } catch { /* handled silently */ }
      } else {
        setIsAuthenticated(false);
      }
      setLoading(false);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
 
  // ── Data-fetch helpers ────────────────────────────────────────────────────
  const fetchData = useCallback(async (imei) => {
    const target = imei || value;
    if (!target) return;
    try {
      const res = await authorizedFetch(`/pump/readings/${target}`);
      if (!res.ok) throw new Error("Network response was not ok");
      setData(await res.json());
    } catch { /* handled silently */ }
  }, [authorizedFetch, value]);
 
  const fetchDashboardData = useCallback(async (timePeriod = duration) => {
    try {
      const res = await authorizedFetch(`/rms_dashboard?days=${timePeriod}`);
      if (!res.ok) throw new Error("Network response was not ok");
      setDashboardData(await res.json());
    } catch { /* handled silently */ }
  }, [authorizedFetch, duration]);
 
  const fetchPumpRunningStatus = useCallback(async (imei, signal) => {
    const target = imei || value;
    if (!target) return;
    try {
      const res = await authorizedFetch(`/running_status/${target}`, {}, signal);
      const result = await res.json();
      setPumpRunningStatus(result.status);
      return result.status;
    } catch (err) {
      if (err.name !== "AbortError") console.error("fetchPumpRunningStatus:", err);
    }
  }, [authorizedFetch, value]);
 
  const fetchPumpStats = useCallback(async (imei) => {
    const target = imei || value;
    if (!target) return;
    try {
      const res = await authorizedFetch(`/pump/${target}`);
      const result = await res.json();
      setPumpStats(result);
      setIsOnline(!!result?.pump_status?.pump_connectivity);
    } catch {
      setError("Failed to fetch pump stats");
    }
  }, [authorizedFetch, value]);
 
  const fetchPumpSessions = useCallback(async (imei) => {
    const target = imei || value;
    if (!target) return;
    try {
      const res = await authorizedFetch(`/pump_sessions/${target}`);
      setPumpSessions(await res.json());
    } catch { /* handled silently */ }
  }, [authorizedFetch, value]);
 
  const fetchLocations = useCallback(async () => {
    try {
      const res = await authorizedFetch(`/pump/location`);
      setPumpLocations(await res.json());
    } catch { /* handled silently */ }
  }, [authorizedFetch]);
 
  // ── changePumpRunningStatus — robust, low-latency ────────────────────────
  /**
   * Toggles the pump on/off and polls until the hardware confirms the change.
   *
   * Design decisions:
   *  - Snapshots all state at call-time to avoid stale-closure bugs.
   *  - Cancels any previous in-flight call via AbortController.
   *  - Uses an adaptive back-off so the first re-check happens after only
   *    POLL_INITIAL_DELAY_MS (300 ms), then grows up to POLL_MAX_INTERVAL_MS.
   *  - Gives up cleanly after POLL_TIMEOUT_MS (30 s).
   *  - All network errors inside the poll loop are retried (up to 3 times)
   *    before being treated as a hard failure.
   */
  const changePumpRunningStatus = useCallback(async () => {
    if (!value) return { success: false, message: "No pump selected" };

    // ── Prevent concurrent calls ──────────────────────────────────────────
    if (statusChangeInFlightRef.current) {
      return { success: false, message: "Operation already in progress" };
    }
    statusChangeInFlightRef.current = true;

    // ── Pause the global auto-refresh so it doesn't overwrite poll state ──
    setAutoRefresh(false);

    // ── Cancel any previous in-flight requests ────────────────────────────
    if (statusChangeAbortRef.current) statusChangeAbortRef.current.abort();
    const abortController = new AbortController();
    statusChangeAbortRef.current = abortController;
    const { signal } = abortController;

    // ── Snapshot state so closures stay correct ───────────────────────────
    const currentImei   = value;
    const currentStatus = pumpRunningStatus;
    const desiredStatus = currentStatus === "on" ? "off" : "on";
    const statusToSend  = desiredStatus === "on";

    const cleanup = () => {
      statusChangeInFlightRef.current = false;
      setAutoRefresh(true); // resume global refresh
    };

    // Helper: abort-safe sleep
    const sleep = (ms) => new Promise((resolve, reject) => {
      const t = setTimeout(resolve, ms);
      signal.addEventListener("abort", () => {
        clearTimeout(t);
        reject(new DOMException("Aborted", "AbortError"));
      }, { once: true });
    });

    try {
      // ── 1. Send toggle command ────────────────────────────────────────────
      const initialResponse = await authorizedFetch(
        `/running_status/${currentImei}`,
        { method: "POST", body: JSON.stringify({ status: statusToSend }) },
        signal
      );

      if (!initialResponse.ok) {
        cleanup();
        return { success: false, message: "Failed to send command to server" };
      }

      const initialResult = await initialResponse.json();

      // Pump is offline — no point polling
      if (
        initialResult.info === "Pump is not connected/online" ||
        initialResult.status === "offline"
      ) {
        cleanup();
        return { success: false, message: "Pump is offline or unreachable" };
      }

      // Instant confirmation (some firmware responds immediately)
      if (initialResult.status === desiredStatus) {
        setPumpRunningStatus(desiredStatus);
        cleanup();
        return { success: true, message: `Pump switched ${desiredStatus} successfully` };
      }

      // ── 2. Optimistic UI: show switching state immediately ────────────────
      // So the user sees feedback right away instead of the old state frozen.
      const switchingState = desiredStatus === "on" ? "switching_on" : "switching_off";
      setPumpRunningStatus(switchingState);

      // ── 3. Adaptive poll until confirmed or timeout ───────────────────────
      const deadline     = Date.now() + POLL_TIMEOUT_MS;
      let   interval     = POLL_INITIAL_DELAY_MS;
      let   networkFails = 0;
      let   revertCount  = 0;   // consecutive old-state readings after seeing switching
      let   seenSwitching = initialResult.status?.startsWith("switching"); // already in switching?
      const MAX_NET_RETRIES = 3;

      while (Date.now() < deadline) {
        if (signal.aborted) { cleanup(); return { success: false, message: "Operation was cancelled" }; }

        await sleep(interval);

        if (signal.aborted) { cleanup(); return { success: false, message: "Operation was cancelled" }; }

        interval = Math.min(interval * POLL_BACKOFF_FACTOR, POLL_MAX_INTERVAL_MS);

        let pollResult;
        try {
          const pollResponse = await authorizedFetch(`/running_status/${currentImei}`, {}, signal);
          if (!pollResponse.ok) {
            if (++networkFails >= MAX_NET_RETRIES) {
              cleanup();
              return { success: false, message: "Server error while checking pump status" };
            }
            continue;
          }
          pollResult   = await pollResponse.json();
          networkFails = 0;
        } catch (pollErr) {
          if (pollErr.name === "AbortError") { cleanup(); return { success: false, message: "Operation was cancelled" }; }
          if (++networkFails >= MAX_NET_RETRIES) {
            cleanup();
            return { success: false, message: "Network error while checking pump status" };
          }
          continue;
        }

        const polledStatus = pollResult.status;

        // Success
        if (polledStatus === desiredStatus) {
          setPumpRunningStatus(desiredStatus);
          cleanup();
          return { success: true, message: `Pump switched ${desiredStatus} successfully` };
        }

        // Device is in mid-switch — mark it and keep going
        if (polledStatus === "switching_on" || polledStatus === "switching_off") {
          seenSwitching = true;
          revertCount   = 0; // reset grace counter while actively switching
          setPumpRunningStatus(polledStatus); // keep UI in sync
          continue;
        }

        // Offline during polling — terminal
        if (polledStatus === "offline" || pollResult.info === "Pump is not connected/online") {
          cleanup();
          return { success: false, message: "Pump went offline during switching" };
        }

        // Old state detected — only declare revert after a grace period
        // so transient device hiccups don't cause false failures.
        if (polledStatus === currentStatus) {
          if (seenSwitching) {
            // We were switching but snapped back — allow a couple of readings
            // before treating it as a confirmed revert.
            if (++revertCount >= POLL_MAX_REVERT_GRACE) {
              setPumpRunningStatus(currentStatus); // restore actual state in UI
              cleanup();
              return { success: false, message: `Pump reverted to ${currentStatus} — command rejected by device` };
            }
            // Stay in the loop: this poll might be a stale reading
          }
          // If we haven't seen switching yet, the device just hasn't started —
          // keep polling until the deadline.
          continue;
        }
      }

      // Deadline exceeded — restore the last known state
      setPumpRunningStatus(currentStatus);
      cleanup();
      return { success: false, message: "Timed out waiting for pump — it may still be switching" };

    } catch (err) {
      cleanup();
      if (err.name === "AbortError") return { success: false, message: "Operation was cancelled" };
      console.error("changePumpRunningStatus:", err);
      return { success: false, message: "Unexpected error — check if pump is manually controlled" };
    }
  }, [authorizedFetch, value, pumpRunningStatus]);
 
  // ── Cancel status-change polling on unmount ───────────────────────────────
  useEffect(() => {
    return () => {
      if (statusChangeAbortRef.current) statusChangeAbortRef.current.abort();
    };
  }, []);
 
  // ── CRUD helpers ──────────────────────────────────────────────────────────
  const createPump = useCallback(async (pumpData) => {
    try {
      const response = await authorizedFetch("/pump", {
        method: "POST",
        body: JSON.stringify(pumpData),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const err = new Error(errorData.message || "Failed to create pump");
        err.response = response;
        throw err;
      }
      const text = await response.text();
      return text ? JSON.parse(text) : { success: true, message: "Pump created successfully" };
    } catch (err) {
      console.error("createPump:", err);
      throw err;
    }
  }, [authorizedFetch]);
 
  const findPump = useCallback(async (searchImei) => {
    try {
      // Consumer-assignment dropdowns need pumps not yet linked to a consumer
      const response = await authorizedFetch('/pump_imei?not_included=true');
      if (!response.ok) return false;
      const list = await response.json();
      setPumpsWithImei(list);
      if (!searchImei) return list;
      const found = list.find((p) => p.imei === searchImei);
      if (!found) return false;
      const res = await authorizedFetch(`/pump/${searchImei}`);
      return await res.json();
    } catch {
      return false;
    }
  }, [authorizedFetch]);
 
  const findPumpToUpdate = useCallback(async (searchImei) => {
    try {
      const response = await authorizedFetch(`/pump/${searchImei}`);
      if (!response.ok) throw new Error("Pump not found");
      return await response.json();
    } catch (err) {
      throw err;
    }
  }, [authorizedFetch]);
 
  const updatePump = useCallback(async (imei, pumpData) => {
    try {
      const response = await authorizedFetch(`/pump/${imei}`, {
        method: "PUT",
        body: JSON.stringify(pumpData),
      });
      if (!response.ok) throw new Error("Failed to update pump");
      return await response.json();
    } catch (err) {
      throw err;
    }
  }, [authorizedFetch]);
 
  const createConsumer = useCallback(async (consumerData) => {
    try {
      const response = await authorizedFetch("/consumer", {
        method: "POST",
        body: JSON.stringify(consumerData),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const err = new Error(errorData.error || "Failed to create consumer");
        err.response = response;
        throw err;
      }
      return await response.json();
    } catch (err) {
      throw err;
    }
  }, [authorizedFetch]);
 
  const updateConsumer = useCallback(async (mobile_number, consumerData) => {
    try {
      const newConsumerData = {
        ...consumerData,
        ...(consumerData.password?.trim() && { password: consumerData.password }),
      };
      const response = await authorizedFetch(`/consumer/${mobile_number}`, {
        method: "PUT",
        body: JSON.stringify(newConsumerData),
      });
      if (!response.ok) throw new Error("Failed to update consumer");
      return await response.json();
    } catch (err) {
      throw err;
    }
  }, [authorizedFetch]);
 
  const createComplaint = useCallback(async (complaintData) => {
    try {
      const response = await authorizedFetch("/consumer/complaint/register", {
        method: "POST",
        body: JSON.stringify(complaintData),
      });
      if (!response.ok) throw new Error("Failed to create complaint");
      return await response.json();
    } catch (err) {
      console.error("createComplaint:", err);
      throw err;
    }
  }, [authorizedFetch]);
 
  const updateComplaint = useCallback(async (pumpImei, complaintData) => {
    try {
      const response = await authorizedFetch(`/complaints/${pumpImei}`, {
        method: "PUT",
        body: JSON.stringify(complaintData),
      });
      if (!response.ok) throw new Error("Failed to update complaint");
      return await response.json();
    } catch (err) {
      console.error("updateComplaint:", err);
      throw err;
    }
  }, [authorizedFetch]);
 
  const getComplaintByTicketNo = useCallback(async (ticketNo) => {
    try {
      const response = await authorizedFetch(`/admin/complaints/${ticketNo}`);
      if (!response.ok) throw new Error("Failed to fetch complaint");
      return await response.json();
    } catch (err) {
      console.error("getComplaintByTicketNo:", err);
      throw err;
    }
  }, [authorizedFetch]);

  const deletePump = useCallback(async (imei) => {
    const response = await authorizedFetch(`/pump/${imei}`, { method: "DELETE" });
    const body = await response.json();
    if (!response.ok) throw new Error(body.error || "Failed to delete pump");
    return body;
  }, [authorizedFetch]);

  const deleteConsumer = useCallback(async (mobileNumber) => {
    const response = await authorizedFetch(`/consumer/${mobileNumber}`, { method: "DELETE" });
    // 204 No Content is a valid success response for DELETE — don't attempt .json()
    if (response.status === 204) return {};
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || body.message || "Failed to delete consumer");
    return body;
  }, [authorizedFetch]);

  const listDashboardUsers = useCallback(async () => {
    const response = await authorizedFetch("/dashboard/users");
    if (!response.ok) throw new Error("Failed to fetch users");
    return await response.json();
  }, [authorizedFetch]);

  const createDashboardUser = useCallback(async (userData) => {
    const response = await authorizedFetch("/signup", {
      method: "POST",
      body: JSON.stringify(userData),
    });
    const body = await response.json();
    if (!response.ok) throw new Error(body.error || "Failed to create user");
    return body;
  }, [authorizedFetch]);

  const deleteDashboardUser = useCallback(async (userId) => {
    const response = await authorizedFetch(`/dashboard/users/${userId}`, { method: "DELETE" });
    const body = await response.json();
    if (!response.ok) throw new Error(body.error || "Failed to delete user");
    return body;
  }, [authorizedFetch]);

  // ── IMEI extraction from URL ──────────────────────────────────────────────
  const location = useLocation();
  const { imei }  = useParams();
 
  useEffect(() => {
    let extractedImei = imei;
    if (!extractedImei && location.pathname.startsWith('/individualPump/')) {
      extractedImei = location.pathname.split('/individualPump/')[1];
    }
    if (extractedImei && value !== extractedImei) {
      setValue(extractedImei);
    }
  }, [imei, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
 
  // ── Boot-time fetch (pump list + locations) ───────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      await Promise.allSettled([findPump(), fetchLocations()]);
    })();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Page-aware data fetching + auto-refresh ───────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || loading) return;

    const path       = location.pathname;
    const isIndividual = path.startsWith("/individualPump") && !!value;
    const isDashboard  = path.startsWith("/dashboard");
    const isGeo        = path.startsWith("/geography");
    const isConsumers  = path.startsWith("/manageConsumers");

    // Full data fetch on mount / dependency change
    const fetchInitialData = () =>
      Promise.allSettled([
        isIndividual && fetchData(value),
        isDashboard  && fetchDashboardData(duration),
        isGeo        && fetchLocations(),
        isIndividual && fetchPumpRunningStatus(value),
        isIndividual && fetchPumpStats(value),
        isIndividual && fetchPumpSessions(value),
        isConsumers  && findPump(),
      ].filter(Boolean));

    // Lightweight auto-refresh: only the things that change quickly
    const fetchAutoRefreshData = () =>
      Promise.allSettled([
        isIndividual && fetchData(value),
        isIndividual && fetchPumpRunningStatus(value),
        isDashboard  && fetchDashboardData(duration),
      ].filter(Boolean));
 
    fetchInitialData();
 
    if (!autoRefresh) return;
 
    const interval = setInterval(fetchAutoRefreshData, 5000);
    return () => clearInterval(interval);
    // value is the critical dep: when the user navigates to a different pump,
    // the interval must restart against the new IMEI.
  }, [autoRefresh, value, duration, location.pathname, isAuthenticated, loading]); // eslint-disable-line react-hooks/exhaustive-deps
 
  // ── Formatting helpers ────────────────────────────────────────────────────
  const formatDuration = (minutes) => {
    const totalSeconds = parseFloat(minutes) * 60;
    const hours = Math.floor(totalSeconds / 3600);
    const mins  = Math.floor((totalSeconds % 3600) / 60);
    return hours > 0 ? `${hours} hr ${mins} min` : `${mins} min`;
  };
 
  const formatDate = (date) => {
    const istDate = new Date(new Date(date).getTime() + 19_800_000);
    return istDate.toLocaleTimeString("en-IN", {
      hour12: true,
      hour: "numeric",
      minute: "2-digit",
    });
  };
 
  return (
    <AuthContext.Provider
      value={{
        isAuthenticated, setIsAuthenticated,
        userRole,
        accessToken, refreshAccessToken,
        setTokens, clearTokens,
        duration, setDuration,
        pumpStatus, setPumpStatus,
        data, setData,
        value, setValue,
        fetchData,
        isOnline, setIsOnline,
        dashboardData, setDashboardData,
        pumps, setPumps,
        consumers, setConsumers,
        complaints, setComplaints,
        fetchPumpSessions,
        fetchDashboardData,
        pumpRunningStatus, setPumpRunningStatus,
        fetchPumpRunningStatus,
        fetchPumpStats,
        pumpStats, setPumpStats,
        pumpSessions, setPumpSessions,
        changePumpRunningStatus,
        createPump, updatePump, deletePump,
        createConsumer, updateConsumer, deleteConsumer,
        createComplaint, updateComplaint,
        error, setError,
        loading,
        formatDuration,
        authorizedFetch,
        findPump,
        pumpsWithImei,
        pumpLocations, setPumpLocations,
        findPumpToUpdate,
        pumpToUpdate, setPumpToUpdate,
        getComplaintByTicketNo,
        listDashboardUsers, createDashboardUser, deleteDashboardUser,
        formatDate,
        autoRefresh, setAutoRefresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
 